import {
  CheckCircleOutlined,
  HeartOutlined,
  MinusOutlined,
  PlusOutlined,
  SafetyOutlined,
  ShareAltOutlined,
  ShoppingCartOutlined,
  StarOutlined,
  TruckOutlined
} from '@ant-design/icons';
import {
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Carousel,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Form,
  Image,
  Input,
  List,
  message,
  Modal,
  Pagination,
  Rate,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  Upload
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n';
import { apiService, Product } from '../services/api';
import '../styles/ProductDetailPage.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface ProductDetailPageProps { }

const ProductDetailPage: React.FC<ProductDetailPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isAuthenticated } = useAuth();
  const { t } = useI18n();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [reviewForm] = Form.useForm();
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewStats, setReviewStats] = useState<any>(null);
  const [canReview, setCanReview] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [currentReviewPage, setCurrentReviewPage] = useState(1);
  const [reviewPageSize] = useState(5);

  // 获取商品详情
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await apiService.getProduct(parseInt(id));
        if (response.success) {
          setProduct(response.product);
        } else {
          message.error(t('productDetail.messages.productNotFound'));
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        message.error(t('productDetail.messages.fetchFailed'));
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  // 获取评价数据
  useEffect(() => {
    const fetchReviewData = async () => {
      if (!id || !isAuthenticated) return;

      try {
        setReviewLoading(true);

        // 获取评价列表
        const reviewsResponse = await apiService.getProductReviews(parseInt(id), currentReviewPage - 1, reviewPageSize);
        if (reviewsResponse.success && Array.isArray(reviewsResponse.data)) {
          setReviews(reviewsResponse.data);
        } else {
          setReviews([]);
        }

        // 获取评价统计
        const statsResponse = await apiService.getProductReviewStats(parseInt(id));
        if (statsResponse.success) {
          setReviewStats(statsResponse.data);
        }

        // 检查是否可以评价
        const canReviewResponse = await apiService.canReviewProduct(parseInt(id));
        if (canReviewResponse.success) {
          setCanReview(canReviewResponse.canReview);
        }

      } catch (error) {
        console.error(t('productDetail.reviews.messages.fetchFailed'), error);
      } finally {
        setReviewLoading(false);
      }
    };

    fetchReviewData();
  }, [id, isAuthenticated, currentReviewPage]);

  // 添加到购物车
  const handleAddToCart = () => {
    if (!product) return;

    if (!isAuthenticated) {
      message.warning(t('productDetail.messages.loginRequired'));
      return;
    }

    addItem(product, quantity);
    message.success(t('productDetail.messages.addToCartSuccess').replace('{name}', product.name));
  };

  // 立即购买
  const handleBuyNow = () => {
    if (!product) return;

    if (!isAuthenticated) {
      message.warning(t('productDetail.messages.loginRequired'));
      return;
    }

    addItem(product, quantity);
    // 触发打开购物车事件
    window.dispatchEvent(new CustomEvent('openCart'));
  };

  // 数量控制
  const handleQuantityChange = (value: number) => {
    if (value < 1) return;
    setQuantity(value);
  };

  // 提交评价
  const handleReviewSubmit = async (values: any) => {
    if (!product) return;

    try {
      // 获取用户ID
      const userStr = localStorage.getItem('user');
      let userId = 0;
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          userId = user.id;
        } catch (e) {
          console.error('解析用户信息失败:', e);
        }
      }

      if (!userId) {
        message.error(t('productDetail.reviews.messages.loginRequired'));
        return;
      }

      // 组合内容：如果有标题，则将标题和内容合并
      const reviewContent = values.title
        ? `【${values.title}】\n${values.content}`
        : values.content;

      const response = await apiService.createProductReview(
        product.id,
        userId,
        values.rating,
        reviewContent,
        values.isAnonymous || false
      );

      if (response.success) {
        message.success(t('productDetail.reviews.messages.submitSuccess'));
        setReviewModalVisible(false);
        reviewForm.resetFields();
        // 刷新评价数据
        const reviewsResponse = await apiService.getProductReviews(parseInt(id!), currentReviewPage - 1, reviewPageSize);
        if (reviewsResponse.success) {
          setReviews(reviewsResponse.data);
        }
      } else {
        message.error(response.message || t('productDetail.reviews.messages.submitFailed'));
      }
    } catch (error) {
      message.error(t('productDetail.reviews.messages.submitFailed'));
      console.error('Error submitting review:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('productDetail.loading')}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Title level={3}>{t('productDetail.notFound')}</Title>
        <Button className="btn-primary-unified" onClick={() => navigate('/')}>
          {t('productDetail.backToHome')}
        </Button>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* 面包屑导航 */}
        <Breadcrumb style={{ marginBottom: '20px' }}>
          <Breadcrumb.Item>
            <Button type="link" onClick={() => navigate('/')}>
              {t('productDetail.breadcrumb.home')}
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Button type="link" onClick={() => navigate('/products')}>
              {t('productDetail.breadcrumb.products')}
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{product.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[24, 24]}>
          {/* 左侧商品图片 */}
          <Col xs={24} lg={12}>
            <Card>
              <div className="product-images">
                {product.mediaGalleryList && product.mediaGalleryList.length > 0 ? (
                  <Carousel
                    dots={{ className: 'custom-dots' }}
                    beforeChange={(from, to) => setSelectedImage(to)}
                  >
                    {product.mediaGalleryList.map((media, index) => (
                      <div key={index}>
                        <Image
                          src={media.ossPath}
                          alt={media.name || product.name}
                          style={{ width: '100%', height: '400px', objectFit: 'contain' }}
                          preview={false}
                        />
                      </div>
                    ))}
                  </Carousel>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '400px',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999'
                  }}>
                    {t('productDetail.images.noImage')}
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {/* 右侧商品信息 */}
          <Col xs={24} lg={12}>
            <div className="product-info">
              <Title level={2} style={{ marginBottom: '8px' }}>
                {product.name}
              </Title>

              <div style={{ marginBottom: '16px' }}>
                <Space>
                  <Rate disabled defaultValue={4.5} />
                  <Text type="secondary">(128 {t('productDetail.info.reviews')})</Text>
                </Space>
              </div>

              <div className="price-section" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff4d4f' }}>
                    NT$ {product.salePrice || product.basePrice}
                  </Text>
                  {product.salePrice && (
                    <Text delete type="secondary" style={{ fontSize: '18px' }}>
                      NT$ {product.basePrice}
                    </Text>
                  )}
                </div>
                {product.salePrice && (
                  <Tag color="red" style={{ marginTop: '8px' }}>
                    {t('productDetail.info.limitedOffer')}
                  </Tag>
                )}
              </div>

              <div className="product-features" style={{ marginBottom: '24px' }}>
                <Space direction="vertical" size="small">
                  <div>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                    <Text>{t('productDetail.info.features.authentic')}</Text>
                  </div>
                  <div>
                    <TruckOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
                    <Text>{t('productDetail.info.features.fastDelivery')}</Text>
                  </div>
                  <div>
                    <SafetyOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                    <Text>{t('productDetail.info.features.returnPolicy')}</Text>
                  </div>
                </Space>
              </div>

              <Divider />

              <div className="quantity-section" style={{ marginBottom: '24px' }}>
                <Text strong style={{ marginRight: '16px' }}>{t('productDetail.info.quantity')}</Text>
                <Space>
                  <Button
                    icon={<MinusOutlined />}
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1}
                  />
                  <Input
                    value={quantity}
                    onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                    style={{ width: '60px', textAlign: 'center' }}
                  />
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => handleQuantityChange(quantity + 1)}
                  />
                </Space>
              </div>

              <div className="action-buttons" style={{ marginBottom: '24px' }}>
                <Space size="middle">
                  <Button
                    type="primary"
                    size="large"
                    icon={<ShoppingCartOutlined />}
                    onClick={handleAddToCart}
                    style={{ flex: 1 }}
                  >
                    {t('productDetail.info.addToCart')}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleBuyNow}
                    style={{ flex: 1 }}
                  >
                    {t('productDetail.info.buyNow')}
                  </Button>
                </Space>
              </div>

              <div className="additional-actions">
                <Space>
                  <Button icon={<HeartOutlined />}>{t('productDetail.info.favorite')}</Button>
                  <Button icon={<ShareAltOutlined />}>{t('productDetail.info.share')}</Button>
                </Space>
              </div>
            </div>
          </Col>
        </Row>

        {/* 商品详情标签页 */}
        <Row style={{ marginTop: '40px' }}>
          <Col span={24}>
            <Card>
              <Tabs defaultActiveKey="description">
                <TabPane tab={t('productDetail.tabs.description')} key="description">
                  <div style={{ padding: '20px 0' }}>
                    <Paragraph style={{ fontSize: '16px', lineHeight: '1.8' }}>
                      {product.description || t('productDetail.specifications.noDescription')}
                    </Paragraph>
                  </div>
                </TabPane>

                <TabPane tab={t('productDetail.tabs.specifications')} key="specifications">
                  <Descriptions bordered column={2} style={{ marginTop: '20px' }}>
                    <Descriptions.Item label={t('productDetail.specifications.name')}>{product.name}</Descriptions.Item>
                    <Descriptions.Item label={t('productDetail.specifications.sku')}>{product.sku}</Descriptions.Item>
                    <Descriptions.Item label={t('productDetail.specifications.category')}>{product.category}</Descriptions.Item>
                    <Descriptions.Item label={t('productDetail.specifications.status')}>
                      <Tag color={product.active ? 'green' : 'red'}>
                        {product.active ? t('productDetail.specifications.inStock') : t('productDetail.specifications.outOfStock')}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </TabPane>

                <TabPane tab={t('productDetail.tabs.reviews')} key="reviews">
                  <div style={{ padding: '20px 0' }}>
                    {/* 评价统计 */}
                    {reviewStats && (
                      <Card style={{ marginBottom: '20px' }}>
                        <Row gutter={16}>
                          <Col span={8}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                                {reviewStats.averageRating || 0}
                              </div>
                              <div>{t('productDetail.reviews.stats.averageRating')}</div>
                              <Rate disabled value={Math.round(reviewStats.averageRating || 0)} />
                            </div>
                          </Col>
                          <Col span={8}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                {reviewStats.totalReviews || 0}
                              </div>
                              <div>{t('productDetail.reviews.stats.totalReviews')}</div>
                            </div>
                          </Col>
                          <Col span={8}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '16px' }}>
                                {t('productDetail.reviews.stats.ratingDistribution')}
                              </div>
                              {reviewStats.ratingDistribution && Object.entries(reviewStats.ratingDistribution).map(([rating, count]) => (
                                <div key={rating} style={{ fontSize: '12px' }}>
                                  {rating}{t('productDetail.reviews.stats.stars')}: {String(count)}{t('productDetail.reviews.stats.reviews')}
                                </div>
                              ))}
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                      <Button
                        type="primary"
                        onClick={() => setReviewModalVisible(true)}
                        disabled={!isAuthenticated || !canReview}
                      >
                        {!isAuthenticated ? t('productDetail.reviews.actions.loginFirst') : !canReview ? t('productDetail.reviews.actions.alreadyReviewed') : t('productDetail.reviews.actions.writeReview')}
                      </Button>
                    </div>

                    {reviewLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : (
                      <>
                        <List
                          itemLayout="vertical"
                          dataSource={reviews}
                          renderItem={(item) => (
                            <List.Item
                              style={{
                                background: '#fafafa',
                                padding: '20px',
                                marginBottom: '16px',
                                borderRadius: '8px',
                                border: '1px solid #f0f0f0'
                              }}
                            >
                              <div>
                                {/* 评价头部 - 用户信息和评分 */}
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '16px',
                                  flexWrap: 'wrap',
                                  gap: '12px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Avatar
                                      size={48}
                                      icon={<StarOutlined />}
                                      style={{ backgroundColor: '#1890ff' }}
                                    />
                                    <div>
                                      <div>
                                        <Text strong style={{ fontSize: '15px' }}>
                                          {item.isAnonymous ? t('productDetail.reviews.list.anonymousUser') : `用户***${item.userId}`}
                                        </Text>
                                        {item.isVerifiedPurchase && (
                                          <Tag color="green" style={{ marginLeft: '8px' }}>
                                            {t('productDetail.reviews.list.verifiedPurchase')}
                                          </Tag>
                                        )}
                                      </div>
                                      <div style={{ marginTop: '4px' }}>
                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                          {new Date(item.createdAt).toLocaleDateString()}
                                        </Text>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <Rate disabled value={item.rating} style={{ fontSize: '18px' }} />
                                  </div>
                                </div>

                                {/* 评价标题 */}
                                {item.title && (
                                  <div style={{
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    marginBottom: '12px',
                                    color: '#333'
                                  }}>
                                    {item.title}
                                  </div>
                                )}

                                {/* 评价内容 */}
                                <div style={{
                                  fontSize: '14px',
                                  lineHeight: '1.6',
                                  color: '#666',
                                  marginBottom: '12px',
                                  padding: '12px',
                                  background: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #e8e8e8'
                                }}>
                                  {item.content}
                                </div>

                                {/* 评价图片 */}
                                {item.images && item.images.length > 0 && (
                                  <div style={{ marginBottom: '12px' }}>
                                    <Space wrap size={8}>
                                      {item.images.map((img: string, index: number) => (
                                        <img
                                          key={index}
                                          src={img}
                                          alt={`review-${index}`}
                                          style={{
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                            cursor: 'pointer'
                                          }}
                                        />
                                      ))}
                                    </Space>
                                  </div>
                                )}


                              </div>
                            </List.Item>
                          )}
                        />

                        {reviews.length > 0 && (
                          <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <Pagination
                              current={currentReviewPage}
                              pageSize={reviewPageSize}
                              total={reviewStats?.totalReviews || 0}
                              onChange={(page) => setCurrentReviewPage(page)}
                              showSizeChanger={false}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </TabPane>
              </Tabs>
            </Card>
          </Col>
        </Row>
      </div>

      {/* 评价模态框 */}
      <Modal
        title={t('productDetail.reviews.modal.title')}
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          reviewForm.resetFields();
        }}
        onOk={() => reviewForm.submit()}
        width={600}
      >
        <Form form={reviewForm} onFinish={handleReviewSubmit} layout="vertical">
          <Form.Item label={t('productDetail.reviews.modal.rating')} name="rating" rules={[{ required: true, message: t('productDetail.reviews.modal.ratingRequired') }]}>
            <Rate />
          </Form.Item>

          <Form.Item label={t('productDetail.reviews.modal.titleLabel')} name="title">
            <Input placeholder={t('productDetail.reviews.modal.titlePlaceholder')} />
          </Form.Item>

          <Form.Item label={t('productDetail.reviews.modal.content')} name="content" rules={[{ required: true, message: '请输入评价内容' }]}>
            <Input.TextArea rows={4} placeholder={t('productDetail.reviews.modal.contentPlaceholder')} />
          </Form.Item>

          <Form.Item label="上传图片" name="images">
            <Upload
              listType="picture-card"
              maxCount={3}
              beforeUpload={() => false} // 阻止自动上传
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>上传图片</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item name="isAnonymous" valuePropName="checked">
            <Checkbox>{t('productDetail.reviews.modal.anonymous')}</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductDetailPage;
